import { useCallback, memo, useMemo } from 'react';
import { Box } from '@mui/material';
import { CampaignOrder } from './CampaignOrder';
import { CampaignActions } from './CampaignActions';
import { Notes } from './Notes';
import { useStickyPosition } from 'src/hooks/useStickyPosition';
import { useLocation, useNavigate } from 'react-router';
import { useCampaignOrderData } from '../hooks';
import { IDraftACampaignProps, useDraftACampaign } from '../hooks/draftACampaign';
import { useCitation } from '../Providers/CitationProvider';

export const CampaignRightSide = memo(() => {
  const navigate = useNavigate();
  const { setError } = useCitation();
  const { state: locationState } = useLocation();
  const { state: citationState } = useCitation();
  const { containerRef, isSticky, stickyTop } = useStickyPosition({
    topOffset: 24
  });

  const { mutateAsync: draftACampaign, isPending, isSuccess, data } = useDraftACampaign();


  const { campaignId, locationId } = locationState as { campaignId: string, locationId: string };

  // Get order data with remove actions enabled
  const tableData = useCampaignOrderData({ enableRemoveActions: true });

  // Calculate total to determine if payment button should be disabled
  const total = useMemo(() => {
    return tableData.reduce((acc, item) => acc + (item.price || 0), 0);
  }, [tableData]);

  const handleOneTimePayment = useCallback(() => {
    navigate('/local-seo/citations/confirm-and-pay', {
      state: { campaignId, locationId }
    });
  }, [campaignId, locationId, navigate]);

  const handleSaveForLater = useCallback(async () => {

    const payload: IDraftACampaignProps = {
      campaign_id: campaignId,
      existingCitations: citationState.selectedExistingCitations,
      availableCitations: citationState.selectedAvailableNewCitations.map((citation) => ({
        citationSite: citation.citationSite,
        citationSiteType: citation.citationSiteType,
      })),
    }

    try {
      const response = await draftACampaign(payload);
      if(response.status === 'success') {
        navigate('/local-seo/citations/list');
        setError({
          status: 'success',
          message: 'Campaign saved successfully'
        });
        return;
      }
    } catch (error) {
      setError({
          status: 'error',
          message: 'Unable to save campaign. Please try again.'
        });
    }

  }, [campaignId, citationState.selectedExistingCitations, citationState.selectedAvailableNewCitations]);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        height: 'max-content',
        position: isSticky ? 'sticky' : 'relative',
        top: isSticky ? `${stickyTop}px` : 'auto',
        flex: { xs: '1 1 100%', md: '0 0 380px' },
        minWidth: 0, // Prevents flex item from overflowing
        maxWidth: { xs: '100%', md: '380px' },
        width: { xs: '100%', md: 'auto' }
      }}
    >
      <CampaignOrder
        data={tableData}
        actions={CampaignActions({
          onOneTimePayment: handleOneTimePayment,
          onSaveForLater: handleSaveForLater,
          primaryActionText: 'One-Time Payment',
          secondaryActionText: 'Save For Later',
          secondaryActionLoading: isPending,
          primaryActionDisabled: total === 0
        })}
      />

      <Notes />
    </Box>
  );
});
